/**
 * OSMDExerciseScene.runScore - how a scale run is laid out on the grand staff.
 *
 * Pure, Phaser-free. The expander decides the notes (pitches, spelling, timing, fingers); this
 * module decides where they sit and writes the MusicXML the scene renders.
 *
 * A scale run is written unmetered:
 *   - no time signature is drawn and no barline but the final double bar; every note has the
 *     same value, the last included, with no rest after it;
 *   - under the hood each beat (a quarter note) is its own measure with a hidden barline, so
 *     beams group by the beat, a line can break between any two beats, and the notes are spaced
 *     evenly. When the run does not fill its last beat, that beat is a shorter measure (its own
 *     hidden time signature), never padded with rests.
 *
 * Register (which octave each hand starts in):
 *   - each hand's run stays on its own staff with the fewest ledger lines (the right hand on the
 *     treble staff, the left on the bass), the left hand starting one or two octaves below the
 *     right; no clef ever changes inside a run;
 *   - "fewest" is, in order: the most ledger lines any note needs (over both hands), the total
 *     over all notes, the ledger lines under each hand's first note, and the right hand nearest
 *     the figure's register anchor. Every 1- and 2-octave run finds a register that needs no
 *     octave sign, and is placed this way;
 *   - a run no register can write without octave signs (the 3- and 4-octave runs) is first kept
 *     on a 61-key keyboard (C2 to C7) where it can (the fewest notes outside it), then placed by
 *     the same ledger-line order;
 *   - whatever still needs more than 3 ledger lines is written under an octave sign: 8va / 8vb,
 *     or 15ma / 15mb where one octave is not enough. The notes keep their sounding pitch (the
 *     MusicXML pitch is the sounding pitch; the octave-shift element moves only the drawing), so
 *     the run is graded on what sounds.
 */

/** The expander's division space: 4 divisions per quarter note. */
export const RUN_DIVISIONS_PER_QUARTER = 4;

/** The meter the transport runs a scale run in: one quarter-note beat per (hidden) measure. */
export const RUN_METER: [number, number] = [1, 4];

/** The most ledger lines a note is written with before it takes an octave sign. */
export const MAX_LEDGER_LINES = 3;

export type RunHand = 'RH' | 'LH';

export type NoteLetter = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';

const LETTERS: readonly NoteLetter[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

/** One written note of the run, in time order within its hand. */
export interface RunScoreNote {
  /** Sounding MIDI pitch. */
  midi: number;
  letter: NoteLetter;
  /** Semitones off the natural letter (-2..2). */
  alteration: number;
  startDivisions: number;
  durationDivisions: number;
  /** The reference finger (1-5), shown above the right hand and below the left. */
  finger?: number;
}

/** The written (sounding) octave of a spelled pitch: B#3 sounds C4 and is written in octave 3. */
export const writtenOctave = (midi: number, alteration: number): number =>
  Math.floor((midi - alteration) / 12) - 1;

/** Diatonic staff position: 7 per octave, C0 = 0. */
export const staffPosition = (midi: number, letter: NoteLetter, alteration: number): number =>
  writtenOctave(midi, alteration) * 7 + LETTERS.indexOf(letter);

/** The staff's bottom and top lines, as staff positions (treble E4..F5, bass G2..A3). */
const STAFF_LINES: Record<RunHand, { bottom: number; top: number }> = {
  RH: { bottom: 4 * 7 + 2, top: 5 * 7 + 3 },
  LH: { bottom: 2 * 7 + 4, top: 3 * 7 + 5 },
};

/** Ledger lines a note at `position` needs on the hand's staff (0 on or next to the staff). */
export const ledgerLines = (position: number, hand: RunHand): number => {
  const { bottom, top } = STAFF_LINES[hand];
  if (position > top) return Math.floor((position - top) / 2);
  if (position < bottom) return Math.floor((bottom - position) / 2);
  return 0;
};

/**
 * The octave sign a note is written under: 0 for none, +1 / +2 for 8va / 15ma (written one or
 * two octaves lower than it sounds), -1 / -2 for 8vb / 15mb.
 */
export const octaveShiftFor = (position: number, hand: RunHand): number => {
  const above = position > STAFF_LINES[hand].top;
  const octaves =
    [0, 1, 2].find(
      (shift) => ledgerLines(position + (above ? -7 : 7) * shift, hand) <= MAX_LEDGER_LINES,
    ) ?? 2;
  return above ? octaves : -octaves;
};

interface HandCost {
  /** Notes past the ledger-line limit (written under an octave sign). */
  overLimit: number;
  maxLedger: number;
  totalLedger: number;
  startLedger: number;
}

const handCost = (positions: readonly number[], hand: RunHand): HandCost => {
  const lines = positions.map((position) => ledgerLines(position, hand));
  return {
    overLimit: lines.filter((count) => count > MAX_LEDGER_LINES).length,
    maxLedger: Math.max(0, ...lines),
    totalLedger: lines.reduce((sum, count) => sum + count, 0),
    startLedger: lines[0] ?? 0,
  };
};

/** The lowest and highest keys of a piano. */
const PIANO_LOWEST = 21;
const PIANO_HIGHEST = 108;

/** The lowest and highest keys of a 61-key keyboard: C2 and C7. */
const KEYBOARD_61_LOWEST = 36;
const KEYBOARD_61_HIGHEST = 96;

export interface RegisterChoice {
  /** The MIDI pitch of each hand's first (lowest) tonic. */
  rhBase: number;
  lhBase: number;
}

/**
 * Each hand's starting octave. `positionsAt(base)` gives the staff positions of the hand's run
 * started on the tonic at MIDI `base`; `span` is the run's range in semitones (from its lowest
 * note to its highest). The left hand starts one or two octaves below the right.
 */
export const chooseRegister = (
  tonicPitchClass: number,
  span: number,
  positionsAt: (base: number, hand: RunHand) => number[],
  rhAnchorMidi: number,
  pitchesAt: (base: number) => number[],
): RegisterChoice => {
  const offKeyboard = (base: number): number =>
    pitchesAt(base).filter((midi) => midi < KEYBOARD_61_LOWEST || midi > KEYBOARD_61_HIGHEST)
      .length;
  const bases = Array.from(
    { length: 9 },
    (_unused, octave) => tonicPitchClass + 12 * octave,
  ).filter((base) => base >= PIANO_LOWEST && base + span <= PIANO_HIGHEST);
  const pairs = bases.flatMap((rhBase) =>
    [12, 24]
      .map((gap) => rhBase - gap)
      .filter((lhBase) => bases.includes(lhBase))
      .map((lhBase) => ({ rhBase, lhBase })),
  );
  const costed = pairs.map((pair) => ({
    pair,
    rh: handCost(positionsAt(pair.rhBase, 'RH'), 'RH'),
    lh: handCost(positionsAt(pair.lhBase, 'LH'), 'LH'),
  }));
  const withoutSigns = costed.filter(({ rh, lh }) => rh.overLimit + lh.overLimit === 0);
  const needSigns = withoutSigns.length === 0;
  const scored = (needSigns ? costed : withoutSigns).map(({ pair, rh, lh }) => {
    return {
      pair,
      key: [
        needSigns ? offKeyboard(pair.rhBase) + offKeyboard(pair.lhBase) : 0,
        Math.max(rh.maxLedger, lh.maxLedger),
        rh.maxLedger + lh.maxLedger,
        rh.totalLedger + lh.totalLedger,
        rh.startLedger + lh.startLedger,
        Math.abs(pair.rhBase - rhAnchorMidi),
      ],
    };
  });
  const [best] = scored.sort((a, b) => {
    const differing = a.key.findIndex((value, index) => value !== b.key[index]);
    return differing < 0 ? 0 : a.key[differing] - b.key[differing];
  });
  if (!best) {
    throw new Error('scale run: no register keeps the run on the keyboard');
  }
  return best.pair;
};

const STEP_TYPES: Record<number, string> = { 4: 'quarter', 2: 'eighth', 1: '16th' };

/** Beam values for note `index` of `count` same-length notes in one beat group. */
const beamsFor = (index: number, count: number, durationDivisions: number): string => {
  if (count < 2 || durationDivisions >= RUN_DIVISIONS_PER_QUARTER) return '';
  const value = index === 0 ? 'begin' : index === count - 1 ? 'end' : 'continue';
  const levels = durationDivisions === 1 ? [1, 2] : [1];
  return levels.map((level) => `<beam number="${level}">${value}</beam>`).join('');
};

/** The beat a note starts in (0-based). */
const beatOf = (startDivisions: number): number =>
  Math.floor(startDivisions / RUN_DIVISIONS_PER_QUARTER);

/**
 * The run's hidden measures, as [start, end) in divisions: one per beat, except that a last
 * beat the run does not fill joins the beat before it, so the last note never sits alone on a
 * line.
 */
export const runMeasureSpans = (endDivisions: number): Array<[number, number]> => {
  const fullBeats = Math.floor(endDivisions / RUN_DIVISIONS_PER_QUARTER);
  const remainder = endDivisions - fullBeats * RUN_DIVISIONS_PER_QUARTER;
  const beats = Array.from({ length: fullBeats }, (_unused, beat): [number, number] => [
    beat * RUN_DIVISIONS_PER_QUARTER,
    (beat + 1) * RUN_DIVISIONS_PER_QUARTER,
  ]);
  if (remainder === 0) return beats.length > 0 ? beats : [[0, Math.max(1, endDivisions)]];
  if (beats.length === 0) return [[0, endDivisions]];
  return [...beats.slice(0, -1), [beats[beats.length - 1][0], endDivisions]];
};

/** The hidden measure (0-based) a note starting at `startDivisions` is written in. */
export const runMeasureIndex = (startDivisions: number, endDivisions: number): number => {
  const spans = runMeasureSpans(endDivisions);
  const found = spans.findIndex(([start, end]) => startDivisions >= start && startDivisions < end);
  return found < 0 ? spans.length - 1 : found;
};

const octaveShiftXml = (
  shift: number,
  kind: 'start' | 'stop',
  staff: number,
  number: number,
): string => {
  const size = Math.abs(shift) === 2 ? 15 : 8;
  // MusicXML: "down" marks notes printed lower than they sound (8va / 15ma), "up" higher.
  const type = kind === 'stop' ? 'stop' : shift > 0 ? 'down' : 'up';
  const placement = shift > 0 ? 'above' : 'below';
  return (
    `<direction placement="${placement}"><direction-type>` +
    `<octave-shift type="${type}" size="${size}" number="${number}"/>` +
    `</direction-type><staff>${staff}</staff></direction>`
  );
};

export interface RunScoreInput {
  keySignatureFifths: number;
  /** Each hand's notes in time order (a hand that does not play is left out). */
  hands: Partial<Record<RunHand, RunScoreNote[]>>;
  showFingering: boolean;
}

export interface RunScore {
  musicXml: string;
  measureCount: number;
  /** Each hand's octave signs, one per note in its order (0 = none). */
  octaveShifts: Partial<Record<RunHand, number[]>>;
}

/**
 * A hand's notes with the octave sign each is written under. A sign covers whole beats: a beat
 * with any note past 3 ledger lines is written under the sign that note needs (the largest in
 * the beat), so the line never zigzags note by note at the threshold.
 */
const shiftsOf = (notes: readonly RunScoreNote[], hand: RunHand): number[] => {
  const needs = notes.map((note) =>
    octaveShiftFor(staffPosition(note.midi, note.letter, note.alteration), hand),
  );
  const beatShift = needs.reduce<Map<number, number>>((byBeat, need, index) => {
    const beat = beatOf(notes[index].startDivisions);
    const held = byBeat.get(beat) ?? 0;
    return new Map(byBeat).set(beat, Math.abs(need) > Math.abs(held) ? need : held);
  }, new Map());
  return notes.map((note) => beatShift.get(beatOf(note.startDivisions)) ?? 0);
};

const noteXml = (
  note: RunScoreNote,
  staff: number,
  voice: number,
  beams: string,
  showFingering: boolean,
): string => {
  const type = STEP_TYPES[note.durationDivisions] ?? 'quarter';
  const alter = note.alteration !== 0 ? `<alter>${note.alteration}</alter>` : '';
  const placement = staff === 1 ? 'above' : 'below';
  const fingering =
    showFingering && note.finger !== undefined
      ? `<notations><technical><fingering placement="${placement}">${note.finger}</fingering></technical></notations>`
      : '';
  return (
    `<note><pitch><step>${note.letter}</step>${alter}` +
    `<octave>${writtenOctave(note.midi, note.alteration)}</octave></pitch>` +
    `<duration>${note.durationDivisions}</duration><voice>${voice}</voice><type>${type}</type>` +
    `<staff>${staff}</staff>${beams}${fingering}</note>`
  );
};

/** A measure's worth of one staff: its notes (with octave-sign starts and stops) or a hidden rest. */
const staffMeasureXml = (
  notes: readonly { note: RunScoreNote; shift: number; previousShift: number; nextShift: number }[],
  staff: number,
  voice: number,
  measureDivisions: number,
  showFingering: boolean,
): string => {
  if (notes.length === 0) {
    return (
      `<note print-object="no"><rest measure="yes"/><duration>${measureDivisions}</duration>` +
      `<voice>${voice}</voice><staff>${staff}</staff></note>`
    );
  }
  return notes
    .map(({ note, shift, previousShift, nextShift }) => {
      const opens = shift !== 0 && shift !== previousShift;
      const closes = shift !== 0 && shift !== nextShift;
      const group = notes.filter(
        (other) => beatOf(other.note.startDivisions) === beatOf(note.startDivisions),
      );
      const beams = beamsFor(
        group.findIndex((other) => other.note === note),
        group.length,
        note.durationDivisions,
      );
      return (
        (opens ? octaveShiftXml(shift, 'start', staff, staff) : '') +
        noteXml(note, staff, voice, beams, showFingering) +
        (closes ? octaveShiftXml(shift, 'stop', staff, staff) : '')
      );
    })
    .join('');
};

/** The time signature element of a measure: hidden, 1/4 for a whole beat, else its length. */
const hiddenTime = (measureDivisions: number): string =>
  measureDivisions === RUN_DIVISIONS_PER_QUARTER
    ? '<time print-object="no"><beats>1</beats><beat-type>4</beat-type></time>'
    : `<time print-object="no"><beats>${measureDivisions}</beats><beat-type>16</beat-type></time>`;

/** Writes the run's MusicXML: a grand staff, hidden measures by the beat, a final double bar. */
export const writeRunScore = (input: RunScoreInput): RunScore => {
  const handOrder: RunHand[] = ['RH', 'LH'];
  const octaveShifts = handOrder.reduce<Partial<Record<RunHand, number[]>>>(
    (shifts, hand) =>
      input.hands[hand] ? { ...shifts, [hand]: shiftsOf(input.hands[hand]!, hand) } : shifts,
    {},
  );
  const allNotes = handOrder.flatMap((hand) => input.hands[hand] ?? []);
  const endDivisions = Math.max(
    0,
    ...allNotes.map((note) => note.startDivisions + note.durationDivisions),
  );
  const spans = runMeasureSpans(endDivisions);
  const measureCount = spans.length;

  const placed = (hand: RunHand) => {
    const notes = input.hands[hand] ?? [];
    const shifts = octaveShifts[hand] ?? [];
    return notes.map((note, index) => ({
      note,
      shift: shifts[index],
      previousShift: index > 0 ? shifts[index - 1] : 0,
      nextShift: index < notes.length - 1 ? shifts[index + 1] : 0,
    }));
  };
  const rh = placed('RH');
  const lh = placed('LH');

  const measures = spans.map(([start, end], index) => {
    const measureDivisions = end - start;
    const inMeasure = (entry: { note: RunScoreNote }) =>
      entry.note.startDivisions >= start && entry.note.startDivisions < end;
    const isFirst = index === 0;
    const isLast = index === measureCount - 1;
    const attributes = isFirst
      ? `<attributes><divisions>${RUN_DIVISIONS_PER_QUARTER}</divisions>` +
        `<key><fifths>${input.keySignatureFifths}</fifths></key>${hiddenTime(measureDivisions)}` +
        `<staves>2</staves><clef number="1"><sign>G</sign><line>2</line></clef>` +
        `<clef number="2"><sign>F</sign><line>4</line></clef></attributes>`
      : measureDivisions !== RUN_DIVISIONS_PER_QUARTER
        ? `<attributes>${hiddenTime(measureDivisions)}</attributes>`
        : '';
    const barline = isLast
      ? '<barline location="right"><bar-style>light-heavy</bar-style></barline>'
      : '<barline location="right"><bar-style>none</bar-style></barline>';
    return (
      `<measure number="${index + 1}">${attributes}` +
      staffMeasureXml(rh.filter(inMeasure), 1, 1, measureDivisions, input.showFingering) +
      `<backup><duration>${measureDivisions}</duration></backup>` +
      staffMeasureXml(lh.filter(inMeasure), 2, 5, measureDivisions, input.showFingering) +
      `${barline}</measure>`
    );
  });

  const musicXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' +
    '<score-partwise version="3.1"><part-list><score-part id="P1">' +
    '<part-name print-object="no">Piano</part-name></score-part></part-list>' +
    `<part id="P1">${measures.join('')}</part></score-partwise>`;
  return { musicXml, measureCount, octaveShifts };
};
